/**
 * Composant - TransactionReceipt
 *
 * Écran de fin d'opération : confirmation, aperçu du reçu officiel et impression
 * (A4, exemplaires client et agence — voir recuImpression.ts).
 */

import { useState } from 'react';
import { CheckCircle, Download, Loader2, Plus, Printer } from 'lucide-react';
import { formatMoney } from '../../../shared/utils/formatters';
import { montantEnLettres } from '../../../shared/utils/montantEnLettres';
import { TransactionResponseDTO } from '../../../application/dto/TransactionResponseDTO';
import logoUnacoopec from '../../assets/logo-unacoopec.jpg';
import './TransactionReceipt.css';
import { imprimerRecu, libelleAgence, lignesBilletage, lignesDeposant, libelleOperation } from './recuImpression';

interface TransactionReceiptProps {
  transaction: TransactionResponseDTO;
  onPrint?: () => void;
  onClose?: () => void;
}

const FORMAT_DATE = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function TransactionReceipt({ transaction: t, onPrint, onClose }: TransactionReceiptProps) {
  const [impression, setImpression] = useState(false);
  const retrait = t.type === 'WITHDRAWAL';
  const billets = lignesBilletage(t);
  const signataires = t.signataires ?? [];

  const imprimer = async () => {
    setImpression(true);
    try {
      await imprimerRecu(t);
      onPrint?.();
    } finally {
      setImpression(false);
    }
  };

  const telechargerBordereau = () => {
    if (!t.receiptPdfBase64) return;
    const lien = document.createElement('a');
    lien.href = `data:application/pdf;base64,${t.receiptPdfBase64}`;
    lien.download = `bordereau-${t.reference}.pdf`;
    lien.click();
  };

  const lignes: [string, React.ReactNode][] = [
    ['Compte', <span key="compte" className="rc-mono">{t.accountNumber}</span>],
    ['Titulaire', t.clientName],
    ...(t.agence ? [['Agence', t.agence] as [string, React.ReactNode]] : []),
    ...lignesDeposant(t),
    ...(signataires.length > 1
      ? signataires.map(
          (s) =>
            [
              `Signataire ${s.ordre}`,
              <span key={`signataire-${s.ordre}`}>
                {s.nom}
                {s.carteMasquee && <span className="rc-carte">{s.carteMasquee}</span>}
              </span>,
            ] as [string, React.ReactNode],
        )
      : signataires.length === 1 && signataires[0].carteMasquee
        ? [['Carte', <span key="carte" className="rc-mono">{signataires[0].carteMasquee}</span>] as [string, React.ReactNode]]
        : []),
    ...(t.destination ? [['Motif', t.destination] as [string, React.ReactNode]] : []),
    ['Caissier', t.cashierName],
  ];

  return (
    <div className="rc">
      {/* Confirmation */}
      <div className="rc-confirmation">
        <div className="rond">
          <CheckCircle />
        </div>
        <div>
          <h2>{retrait ? 'Retrait effectué' : 'Versement enregistré'}</h2>
          <p>{retrait ? 'Remettez les espèces et le reçu au client.' : 'Remettez le reçu au déposant.'}</p>
        </div>
      </div>

      {/* Aperçu du reçu */}
      <article className="rc-papier" aria-label="Aperçu du reçu">
        <header className="rc-entete">
          <img src={logoUnacoopec} alt="UNACOOPEC" />
          <div className="org">
            <strong>UNACOOPEC</strong>
            <span>{libelleAgence(t.agence)}</span>
          </div>
          <div className="ref">
            <strong>{t.reference}</strong>
            {FORMAT_DATE.format(new Date(t.createdAt))}
          </div>
        </header>

        <div className="rc-corps">
          <p className="rc-type">Reçu de {libelleOperation(t).toLowerCase()}</p>

          <div className="rc-montant">
            <p className="lib">Montant {retrait ? 'retiré' : 'versé'}</p>
            <p className="val">{formatMoney(t.amount)}</p>
            <p className="lettres">{montantEnLettres(t.amount)}</p>
          </div>

          <dl className="rc-infos">
            {lignes.map(([libelle, valeur]) => (
              <div key={libelle}>
                <dt>{libelle}</dt>
                <dd>{valeur}</dd>
              </div>
            ))}
          </dl>

          {billets.length > 0 && (
            <table className="rc-billets">
              <thead>
                <tr>
                  <th>Coupure</th>
                  <th>Nombre</th>
                  <th>Montant</th>
                </tr>
              </thead>
              <tbody>
                {billets.map((l) => (
                  <tr key={l.coupure}>
                    <td>{formatMoney(l.coupure)}</td>
                    <td>{l.nombre}</td>
                    <td>{formatMoney(l.coupure * l.nombre)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <footer className="rc-pied">
          Réf. BIOTRANSAC {t.biotransacRef}
          {retrait && ' · Opération authentifiée par carte et empreinte digitale'}
        </footer>
      </article>

      {/* Actions */}
      <div className="rc-actions">
        <button type="button" onClick={imprimer} disabled={impression} className="rc-btn principal">
          {impression ? <Loader2 className="rc-tourne" /> : <Printer />}
          Imprimer le reçu
        </button>
        {t.receiptPdfBase64 && (
          <button type="button" onClick={telechargerBordereau} className="rc-btn" title="Bordereau PDF généré par le serveur">
            <Download />
            Bordereau PDF
          </button>
        )}
        {onClose && (
          <button type="button" onClick={onClose} className="rc-btn">
            <Plus />
            Nouvelle opération
          </button>
        )}
      </div>
      <p className="rc-note">Le reçu s&apos;imprime en deux exemplaires : client et agence.</p>
    </div>
  );
}
